import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HiOutlineCheck } from "react-icons/hi2";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getSingleCheckboxCheckedTimesheet } from "store/selector/rostering.selector";
import { apiGetRequest, apiPostRequest } from "helpers/Requests";
import {
  URL_ROSTERING_GET_SHIF_TASK,
  URL_ROSTERING_UPDATE_SHIFT_TASK,
} from "store/services/URL";
import Loader from "components/common/Loader/Loader";
import TimesheetTabContentShell from "../../../components/TimesheetRevamp/TimesheetTabContentShell";
import { TimesheetTasksTabSkeleton } from "../../../components/TimesheetRevamp/TimesheetDetailTabSkeletons";
import { TimesheetProgressHeader } from "../../../components/TimesheetRevamp/TimesheetTabPrimitives";
import { capitalizeAllLetters } from "Utils/utils";

export const isShiftTaskComplete = (task) =>
  task?.is_completed === true ||
  task?.is_completed === 1 ||
  task?.is_completed === "1";

const normalizeShiftTask = (task) => ({
  ...task,
  is_completed: isShiftTaskComplete(task),
});

const formatParticipantName = (firstName, lastName) => {
  const fullName = `${capitalizeAllLetters(firstName || "")} ${capitalizeAllLetters(
    lastName || ""
  )}`.trim();
  return fullName || null;
};

const getTaskParticipantName = (task, participantNameById = new Map()) => {
  const ptId =
    task?.pt_id != null && task?.pt_id !== "" ? Number(task.pt_id) : null;

  if (ptId != null && !Number.isNaN(ptId) && participantNameById.has(ptId)) {
    return participantNameById.get(ptId);
  }

  return (
    formatParticipantName(
      task?.first_name ?? task?.participant?.first_name,
      task?.last_name ?? task?.participant?.last_name
    ) || null
  );
};

const ShiftTasks = ({
  isDisabledButtons,
  timesheetUuid,
  participants = [],
}) => {
  const selectedTimesheet = useSelector(getSingleCheckboxCheckedTimesheet);
  const resolvedTimesheetUuid = timesheetUuid ?? selectedTimesheet?.uuid;

  const [tasks, setTasks] = useState([]);
  const [loader, setLoader] = useState(false);
  const [updateLoader, setUpdateLoader] = useState(null);

  const participantNameById = useMemo(() => {
    const map = new Map();
    const lists = [
      participants,
      selectedTimesheet?.participants,
    ].filter((list) => Array.isArray(list) && list.length > 0);

    lists.forEach((list) => {
      list.forEach((participant) => {
        const id = Number(participant?.id);
        if (id == null || Number.isNaN(id) || map.has(id)) return;
        const name = formatParticipantName(
          participant?.first_name,
          participant?.last_name
        );
        if (name) {
          map.set(id, name);
        }
      });
    });

    return map;
  }, [participants, selectedTimesheet?.participants]);

  const getShiftTasks = useCallback(() => {
    if (!resolvedTimesheetUuid) return;

    setLoader(true);
    apiGetRequest(`${URL_ROSTERING_GET_SHIF_TASK}/${resolvedTimesheetUuid}`)
      .then((response) => {
        const rows = response?.data?.data ?? [];
        setTasks(Array.isArray(rows) ? rows.map(normalizeShiftTask) : []);
      })
      .catch((error) => {
        // Mobile-created timesheets often have no shift tasks (or a soft-deleted
        // shift). Treat 404 / empty as an empty tab — not a load failure.
        const status = error?.response?.status ?? error?.status;
        if (status === 404) {
          setTasks([]);
          return;
        }
        console.error("Error fetching shift tasks:", error);
        toast.error("Could not load shift tasks");
      })
      .finally(() => {
        setLoader(false);
      });
  }, [resolvedTimesheetUuid]);

  useEffect(() => {
    getShiftTasks();
  }, [getShiftTasks]);

  const updateTaskStatus = (task, nextCompleted) => {
    if (isDisabledButtons || !task?.id) return;

    setUpdateLoader(task.id);
    apiPostRequest(URL_ROSTERING_UPDATE_SHIFT_TASK, {
      id: task.id,
      is_completed: nextCompleted,
    })
      .then((response) => {
        const updated = response?.data?.shift_task;
        if (!updated?.id) {
          throw new Error("Invalid shift task response");
        }

        setTasks((prev) =>
          prev.map((row) =>
            row.id === updated.id
              ? normalizeShiftTask({ ...row, ...updated })
              : row
          )
        );

        toast.success(
          nextCompleted ? "Task marked as complete" : "Task marked as incomplete"
        );
      })
      .catch((error) => {
        console.error("Error updating shift task:", error);
        toast.error("Could not update shift task");
      })
      .finally(() => {
        setUpdateLoader(null);
      });
  };

  const handleToggleTask = (task) => {
    updateTaskStatus(task, !isShiftTaskComplete(task));
  };

  const completedCount = useMemo(
    () => tasks.filter((task) => task.is_completed).length,
    [tasks]
  );
  const openCount = tasks.length - completedCount;
  const percent =
    tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;

  const taskGroups = useMemo(() => {
    const uniquePtIds = [
      ...new Set(
        tasks
          .map((task) =>
            task?.pt_id != null && task?.pt_id !== ""
              ? Number(task.pt_id)
              : null
          )
          .filter((id) => id != null && !Number.isNaN(id))
      ),
    ];

    // Single participant (or no pt_id): still show their name when known.
    if (uniquePtIds.length <= 1) {
      const onlyPtId = uniquePtIds[0] ?? null;
      const title =
        (onlyPtId != null && participantNameById.get(onlyPtId)) ||
        getTaskParticipantName(tasks[0], participantNameById) ||
        (participantNameById.size === 1
          ? [...participantNameById.values()][0]
          : null);

      return [{ key: onlyPtId != null ? String(onlyPtId) : "all", title, tasks }];
    }

    const groupsByPtId = new Map();

    tasks.forEach((task) => {
      const ptId =
        task?.pt_id != null && task?.pt_id !== ""
          ? Number(task.pt_id)
          : null;
      const key = ptId != null && !Number.isNaN(ptId) ? String(ptId) : "unassigned";
      const existing = groupsByPtId.get(key);

      if (existing) {
        existing.tasks.push(task);
        return;
      }

      groupsByPtId.set(key, {
        key,
        title:
          getTaskParticipantName(task, participantNameById) ||
          (key === "unassigned" ? "Other tasks" : null),
        tasks: [task],
      });
    });

    return Array.from(groupsByPtId.values());
  }, [tasks, participantNameById]);

  const renderTaskCard = (task) => {
    const isComplete = task.is_completed;
    const isUpdating = updateLoader === task.id;

    return (
      <div
        key={task.id}
        className={`td-task-card ${isComplete ? "is-complete" : ""}`}
      >
        {isUpdating ? (
          <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center">
            <Loader cssClass="!mr-0 !text-[#4A6BB6]" />
          </span>
        ) : (
          <button
            type="button"
            onClick={() => handleToggleTask(task)}
            disabled={isDisabledButtons}
            className={`flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full border-2 ${
              isComplete
                ? "border-[#0DBBB3] bg-[#0DBBB3]"
                : "border-[#64748B] bg-[#F8FAFC]"
            } ${
              isDisabledButtons
                ? "cursor-not-allowed opacity-60"
                : "cursor-pointer hover:border-[#0DBBB3] hover:bg-[#F0FDFA]"
            }`}
            aria-label={
              isComplete ? "Mark incomplete" : "Mark complete"
            }
            title={isComplete ? "Mark incomplete" : "Mark complete"}
            aria-checked={isComplete}
            role="checkbox"
          >
            {isComplete ? (
              <HiOutlineCheck className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
            ) : null}
          </button>
        )}

        <div className="min-w-0 flex-1">
          <p
            className={`td-task-name text-[13px] font-semibold ${
              isComplete ? "text-[#64748B]" : "text-[#0F172A]"
            }`}
          >
            {task.task}
          </p>
        </div>

        {!isUpdating && (
          <button
            type="button"
            onClick={() =>
              updateTaskStatus(task, !isComplete)
            }
            disabled={isDisabledButtons}
            className={`rounded-lg px-2.5 py-1.5 text-[11px] font-semibold ${
              isComplete
                ? "border border-[#E5E7EB] bg-white text-[#64748B] hover:bg-[#F8FAFC]"
                : "bg-[#EEF2FB] text-[#4A6BB6] hover:bg-[#E2EAF8]"
            } ${
              isDisabledButtons
                ? "cursor-not-allowed opacity-60"
                : ""
            }`}
          >
            {isComplete ? "Mark incomplete" : "Mark complete"}
          </button>
        )}
      </div>
    );
  };

  return (
    <TimesheetTabContentShell
      loading={loader}
      loadingSkeleton={TimesheetTasksTabSkeleton}
      empty={!loader && tasks.length === 0}
      emptyMessage="No shift tasks assigned for this shift."
    >
      <div className="space-y-3">
        <TimesheetProgressHeader
          title="Shift tasks"
          subtitle={
            tasks.length > 0
              ? `${completedCount} of ${tasks.length} completed · ${openCount} still open`
              : "No tasks on this shift"
          }
          pillLabel={tasks.length > 0 ? `${percent}%` : null}
          percent={percent}
        />

        <div className="space-y-4">
          {taskGroups.map((group) => (
            <div key={group.key} className="space-y-2">
              {group.title && (
                <p className="text-[12px] font-semibold text-[#334155]">
                  Tasks for {group.title}
                </p>
              )}
              {group.tasks.map((task) => renderTaskCard(task))}
            </div>
          ))}
        </div>
      </div>
    </TimesheetTabContentShell>
  );
};

export default ShiftTasks;
